import { motion } from "framer-motion";
import { BarChart3, CalendarCheck, Globe, Megaphone, MessageCircle, Play, Star } from "lucide-react";
import { EASE } from "./shared";
import { LeadForm } from "./LeadForm";

const CHIPS = [
    [Megaphone, "Google & Meta Ads", "hero-chip-ads"],
    [Play, "AI Video Ads", "hero-chip-video"],
    [Star, "Collect Google Reviews", "hero-chip-reviews"],
    [MessageCircle, "Reply to reviews with AI", "hero-chip-reply"],
    [Globe, "Purple Page", "hero-chip-page"],
    [CalendarCheck, "Planner", "hero-chip-planner"],
    [BarChart3, "Reports", "hero-chip-reports"],
];

export const Hero = ({ businessName, onBusinessName }) => (
    <section className="relative overflow-hidden pt-36 sm:pt-48" data-testid="hero-section">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
            <div className="mx-auto max-w-3xl text-center">
                <motion.h1
                    initial={{ opacity: 0, y: 28 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
                    className="font-display text-4xl font-bold leading-[1.12] tracking-[-0.02em] text-ink sm:text-5xl"
                >
                    Simplest app to manage your Google Profile
                    <img src="/assets/icon-gbp.png" alt="Google Business Profile" className="mx-1.5 inline-block h-[1.35em] w-auto rounded-full align-[-0.22em]" />
                    , run Google & Meta ads
                    <img src="/assets/icon-ads.png" alt="Google Ads and Meta" className="mx-1.5 inline-block h-[1.35em] w-auto rounded-full align-[-0.22em]" />
                    {" "}and create stunning AI ads.
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
                    className="mx-auto mt-6 max-w-xl text-base text-neutral-600 sm:text-lg"
                >
                    Explore a new chapter of growth with Purple Business.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1, ease: EASE }}
                    className="mx-auto mt-9 max-w-md rounded-[2rem] border border-neutral-200 bg-white p-5 text-left shadow-xl sm:p-7"
                >
                    <LeadForm businessName={businessName} onBusinessName={onBusinessName} idPrefix="hero-lead" />
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
                className="mt-14 flex flex-wrap items-center justify-center gap-3 lg:justify-start"
            >
                {CHIPS.map(([Icon, label, id], i) => (
                    <motion.div
                        key={id}
                        data-testid={id}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 1.2 + i * 0.07, ease: EASE }}
                        whileHover={{ y: -4 }}
                        className="flex items-center gap-2 rounded-2xl border border-neutral-200 bg-white px-5 py-3 text-sm font-bold text-ink shadow-sm"
                    >
                        <Icon className="h-4 w-4 text-ink" />
                        {label}
                    </motion.div>
                ))}
            </motion.div>
        </div>

        <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.9, ease: EASE }}
            className="mx-auto mt-14 w-full max-w-[1320px] px-3 sm:px-5"
        >
            <div className="overflow-hidden rounded-[2rem] sm:rounded-[3rem]">
                <img
                    src="/assets/hero-wide.webp"
                    alt="Small business owners, with a Purple Business owner at the centre"
                    className="h-auto w-full"
                    data-testid="hero-image"
                />
            </div>
        </motion.div>
    </section>
);
